import { Box, Stack, Typography } from '@mui/material';
import { PoiFormSubHeading } from '../styles';
import { useFetchWorkscopeDefects } from '../../hooks/useFetchWorkscopeDefects';
import { useAtom } from 'jotai';
import * as poiState from '../../../../state';
import { PointOfInterest, PointOfInterestTemplateTypeEnum } from '@/__generated__/graphql';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { LinkedPoiEditor } from '../LinkedPoiEditor';
import { useTriggerPoiEditing } from '../../hooks';
import { SaveCancelFormButtons } from '../SaveCancelFormButtons';
import { saveCancelButtonCommonStyles } from '../../../styles';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import { StyledForm } from '../UpdateGeneralFields/styles';
import { useAnalysisCuboidActions } from '@/components/Analysis/modules/cuboids/hooks';
import { roundNumber } from '@/utils/unitSystem';

const getUnitDisplay = (unit: string | null | undefined) => {
  switch (unit) {
    case null:
      return '';
    case undefined:
      return '';
    case 'SQUARE_METER':
      return 'm²';
    case 'SQUARE_FOOT':
      return 'ft²';
    default:
      return unit;
  }
};

export const VoiCreation = () => {
  const [matchingKeys, setMatchingKeys] = useState<string[]>([]);
  const [editPointOfInterest, setEditPointOfInterest] = useAtom(poiState.editPointOfInterest);
  const { cancelEditing } = useTriggerPoiEditing();
  const navbarHeight = useNavbarHeight();
  const { stopAddingCuboid, doneAddingCuboid } = useAnalysisCuboidActions();

  // When creating the POI, there will be no 'id' property on it since that POI is not yet saved to the database
  // But when editing, the ID field will be there, so that can be used to determine if we are editing or creating
  const isEditingVoi = !!editPointOfInterest.pointOfInterest?.id;

  const updateTemplateFieldData = useCallback(
    (update: Record<string, number>) => {
      setEditPointOfInterest((prev) => ({
        ...prev,
        pointOfInterest: {
          ...prev.pointOfInterest,
          templateFieldData: {
            ...(prev.pointOfInterest?.templateFieldData ?? {}),
            ...update,
          },
        } as PointOfInterest,
      }));
    },
    [setEditPointOfInterest]
  );

  const onCancel = () => {
    cancelEditing();
    stopAddingCuboid();
  };

  const isVoi =
    editPointOfInterest.template?.type === PointOfInterestTemplateTypeEnum.VolumeOfInterest;

  const { data, loading } = useFetchWorkscopeDefects({
    skip: !isVoi,
  });

  const defectsWithLowercaseKeys: Record<string, number> | undefined = useMemo(() => {
    if (!data?.dynamicBlockCalculation) return undefined;

    const result: Record<string, number> = {};
    Object.keys(data.dynamicBlockCalculation).forEach((key) => {
      result[key.toLowerCase()] = data.dynamicBlockCalculation[key];
    });
    return result;
  }, [data?.dynamicBlockCalculation]);

  // Populating POIs template field data with defects data
  useEffect(() => {
    // Check to see if the data has been fetched, and fields exist too
    if (defectsWithLowercaseKeys && editPointOfInterest.template?.fields) {
      // Create an update object with data from defects
      // it would look like this { totalArea: 1.32, ri5: 2.2}
      const update: Record<string, number> = editPointOfInterest.template?.fields.reduce(
        (acc, field) => {
          const value = defectsWithLowercaseKeys[String(field.key).toLowerCase()];
          const numericValue = value ? roundNumber(value, 3) : 0;
          if (field.key) {
            acc[field.key] = numericValue;
          }
          return acc;
        },
        {} as Record<string, number>
      );
      updateTemplateFieldData(update);
    }
  }, [defectsWithLowercaseKeys, editPointOfInterest.template?.fields, updateTemplateFieldData]);

  const matchingKeysData = useMemo(() => {
    if (!data?.dynamicBlockCalculation) return [];
    const templateFieldKeys = (editPointOfInterest.template?.fields ?? []).map((item) =>
      String(item.key).toLowerCase()
    );
    const defectKeys = Object.keys(data.dynamicBlockCalculation).map((item) =>
      String(item).toLowerCase()
    );
    return templateFieldKeys.filter((key) => defectKeys.includes(key));
  }, [data?.dynamicBlockCalculation, editPointOfInterest.template?.fields]);

  useEffect(() => {
    if (!matchingKeysData.length) return;
    setMatchingKeys((prev) => (prev?.length ? prev : matchingKeysData));
    setEditPointOfInterest((prev) =>
      prev.voiDefectFieldKeys?.length ? prev : { ...prev, voiDefectFieldKeys: matchingKeysData }
    );
  }, [matchingKeysData, setEditPointOfInterest]);

  const fieldByKey = useMemo(() => {
    const map: Record<string, { name: string; unit?: string | null }> = {};
    (editPointOfInterest.template?.fields ?? []).forEach((field) => {
      if (field.key) {
        map[String(field.key).toLowerCase()] = { name: field.name, unit: field.unit };
      }
    });
    return map;
  }, [editPointOfInterest.template?.fields]);

  const handleNext = useCallback(() => {
    setEditPointOfInterest((current) => ({
      ...current,
      formState: isEditingVoi ? 'Update-General' : 'Create',
    }));
    doneAddingCuboid();
  }, [doneAddingCuboid, isEditingVoi, setEditPointOfInterest]);

  return (
    <Box height="100%">
      <StyledForm
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
      >
        <Stack
          sx={{
            height: `calc(100vh / var(--zoom) - ${navbarHeight}px)`,
            overflowY: 'auto',
            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Box p={2} flex={1} mb="85px">
            <PoiFormSubHeading variant="h5">VOI Creation</PoiFormSubHeading>

            {/* Matching defect fields summary */}
            {loading && !matchingKeys.length ? (
              <Typography fontSize="1.4rem" color="text.secondary" mt={1}>
                Loading...
              </Typography>
            ) : (
              matchingKeys.map((key) => {
                const value = defectsWithLowercaseKeys?.[key];
                const field = fieldByKey[key];
                const displayValue =
                  value !== undefined
                    ? `${value.toFixed(3)}${field?.unit ? ` ${getUnitDisplay(field.unit)}` : ''}`
                    : loading
                      ? '...'
                      : 'N/A';

                return (
                  <Box key={key} mt={2} mb={1}>
                    <Typography fontWeight={700} fontSize="1.6rem">
                      {displayValue}
                    </Typography>
                    {field?.name && (
                      <Typography fontSize="1.4rem" color="primary.main">
                        {field.name}
                      </Typography>
                    )}
                  </Box>
                );
              })
            )}

            {/* Linked POIs section */}
            <Box mt={2}>
              <PoiFormSubHeading variant="h5" sx={{ mb: 1 }}>
                Linked point of interests
              </PoiFormSubHeading>
              <LinkedPoiEditor />
            </Box>
          </Box>

          <SaveCancelFormButtons
            cancelButtonLabel="Cancel"
            buttonLabel="Next"
            onCancel={onCancel}
            sx={saveCancelButtonCommonStyles.sx}
            saveButtonStyles={saveCancelButtonCommonStyles.saveButtonStyles}
            cancelButtonStyles={saveCancelButtonCommonStyles.cancelButtonStyles}
            saveButtonDisabled={loading}
          />
        </Stack>
      </StyledForm>
    </Box>
  );
};
